import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, Activity } from 'lucide-react';
import { getApiBase } from '../apiBase';

const API = axios.create({ baseURL: getApiBase(), withCredentials: true, timeout: 90000 });

export default function ConnectionHealthPanel({ onSessionLost }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function probe() {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/api/hcm/connection-health', { validateStatus: () => true });
      if (res.status === 200) setData(res.data);
      else if (res.status === 401 && (res.data?.code === 'AUTH_REQUIRED' || res.data?.error === 'Not logged in')) {
        setError('Session expired — please sign in again.');
        onSessionLost?.();
      } else setError(res.data?.error || `HTTP ${res.status}`);
    } catch (e) {
      setError(e.message || 'Health check failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    probe();
  }, []);

  const checks = data?.checks || {};

  function Row({ label, ok, detail }) {
    const Icon = ok ? CheckCircle2 : XCircle;
    const tone = ok ? 'text-emerald-600' : 'text-rose-600';
    return (
      <div className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${tone}`} />
        <div>
          <div className="font-medium text-slate-800">{label}</div>
          <div className="text-xs text-slate-500">{detail}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Activity className="h-5 w-5 text-cyan-600" />
        <h3 className="font-semibold text-slate-900">Connection health</h3>
        <button
          type="button"
          onClick={probe}
          disabled={loading}
          className="ml-auto text-xs font-medium text-cyan-700 hover:text-cyan-900 disabled:opacity-50"
        >
          {loading ? <Loader2 className="inline h-3 w-3 animate-spin" /> : null} Re-probe
        </button>
      </div>
      {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}
      {data && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">
            {data.connection?.name || 'Active connection'} · {data.elapsedMs}ms
          </p>
          <Row
            label="BIP SQL runner"
            ok={checks.bipRunner?.ok}
            detail={checks.bipRunner?.message || checks.bipRunner?.reportPath || '—'}
          />
          <Row
            label="SCIM Users"
            ok={checks.scimUsers?.ok}
            detail={
              checks.scimUsers?.ok
                ? `HTTP ${checks.scimUsers.status}`
                : checks.scimUsers?.error || `HTTP ${checks.scimUsers?.status}`
            }
          />
          <Row
            label="publicWorkers"
            ok={checks.publicWorkers?.ok}
            detail={
              checks.publicWorkers?.ok
                ? `HTTP ${checks.publicWorkers.status}`
                : checks.publicWorkers?.error || `HTTP ${checks.publicWorkers?.status}`
            }
          />
        </div>
      )}
    </div>
  );
}
